import React from 'react'
import PropTypes from 'prop-types'
import PlusMinus from './PlusMinus'

export default class AccordionSection extends React.Component {
    // Use `props.open` to control collapsed state via props,
    // leave it `undefined` to keep collapsed state controlled internally.
    // `props.open` overrides `props.initiallyClosed`.
    static getDerivedStateFromProps({ open }) {
        if (typeof open === 'boolean') {
            return { collapsed: !open }
        }
        return null
    }

    constructor(props) {
        super(props)
        this.state = {
            collapsed: this.props.initiallyClosed
        }
        this.bodyElement = React.createRef()
    }

    componentDidMount() {
        const { open, initiallyClosed } = this.props

        let collapsed
        if (typeof open === 'boolean') {
            collapsed = !open
        } else {
            collapsed = initiallyClosed
        }

        if (collapsed) {
            this.setBodyToCollapsed()
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { collapsed } = this.state

        if (prevState.collapsed !== collapsed) {
            if (collapsed) {
                this.collapseBody()
            } else {
                this.expandBody()
            }
        }
    }

    setBodyToCollapsed() {
        // bypasses animations
        const body = this.bodyElement.current

        body.style.height = '0px'
        body.style.visibility = 'hidden'
    }

    collapseBody() {
        const body = this.bodyElement.current

        // get the height of the element's inner content, regardless of its actual size
        const sectionHeight = body.scrollHeight

        // temporarily disable all css transitions
        const elementTransition = body.style.transition
        body.style.transition = ''

        // on the next frame (as soon as the previous style change has taken effect),
        // explicitly set the element's height to its current pixel height, so we
        // aren't transitioning out of 'auto'
        requestAnimationFrame(function() {
            body.style.height = `${sectionHeight}px`
            body.style.transition = elementTransition

            // on the next frame (as soon as the previous style change has taken effect),
            // have the element transition to height: 0
            requestAnimationFrame(function() {
                body.style.height = '0px'
            })
        })

        // set visibility to 'hidden' when the transition finishes
        body.addEventListener(
            'transitionend',
            () => {
                // check state here in case transition is cancelled instead of ending
                if (this.state.collapsed) {
                    body.style.visibility = 'hidden'
                }
            },
            { once: true }
        )
    }

    expandBody() {
        const body = this.bodyElement.current

        // show element again before the transition starts
        body.style.visibility = 'visible'

        // get the height of the element's inner content, regardless of its actual size
        const sectionHeight = body.scrollHeight

        // have the element transition to the height of its inner content
        body.style.height = `${sectionHeight}px`

        // when the next css transition finishes (which should be the one we just triggered)
        body.addEventListener(
            'transitionend',
            () => {
                // remove "height" from the element's inline styles, so it can return to its
                // initial value; check state here in case transition is cancelled instead of ending
                if (!this.state.collapsed) {
                    body.style.height = null
                }
            },
            { once: true }
        )
    }

    toggleCollapse() {
        this.props.onClick()
        if (typeof this.props.open === 'boolean') {
            return
        }

        this.setState(state => ({
            collapsed: !state.collapsed
        }))
    }

    generateHeaderDescription() {
        const getNodeText = node => {
            if (['string', 'number'].includes(typeof node)) return node
            if (node instanceof Array) return node.map(getNodeText).join(' ')
            if (typeof node === 'object' && node) return getNodeText(node.props.children)
        }

        const title = getNodeText(this.props.heading)
        const countDescription =
            this.props.count && this.props.count > 0 ? `(${this.props.count} layer(s) selected)` : ''
        if (this.state.collapsed) {
            return `${title} ${countDescription}: click to expand this section's layers`
        } else {
            return `${title}: click to collapse this section's layers`
        }
    }

    render() {
        return (
            <div className="accordion-section">
                <div
                    className={`accordion-section-header ${this.state.collapsed ? 'collapsed' : ''}`}
                    onClick={() => this.toggleCollapse()}
                    onKeyDown={e => (e.key === 'Enter' ? this.toggleCollapse() : null)}
                    role="button"
                    aria-label={this.generateHeaderDescription()}
                    tabIndex="0"
                >
                    {this.props.heading}
                    <div className="icons">
                        {this.props.count !== undefined && (
                            <div className={`count ${this.props.count === 0 ? 'zero' : ''}`}>
                                <span>{this.props.count}</span>
                            </div>
                        )}

                        <PlusMinus />
                    </div>
                </div>
                <div className="accordion-section-body" ref={this.bodyElement}>
                    {this.props.body}
                </div>
            </div>
        )
    }
}

AccordionSection.propTypes = {
    heading: PropTypes.node.isRequired,
    count: PropTypes.number,
    body: PropTypes.element.isRequired,
    initiallyClosed: PropTypes.bool,
    open: PropTypes.bool,
    onClick: PropTypes.func
}

AccordionSection.defaultProps = {
    count: undefined,
    initiallyClosed: false,
    open: undefined,
    onClick: () => {}
}
